import React from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { resolveApiAssetUrl } from '../../lib/api';
import {
  isRepeatable, money, periodLabel, verticalTitle,
} from './LossBonus';
import './lossBonusTile.css';

/*
 * Kayıp bonusunun Bonuslar sayfasındaki kartı (pages/Promotions.jsx
 * `LOSS_BONUS_ON_PROMOTIONS_THEMES`). Kampanya kartlarının arasında ilk sırada
 * durur ve onlarla aynı iskeleti kullanır: görselin yerinde tema
 * değişkenlerinden çizilen bir kapak, altında "Bonusu Al / Detay". Ayrıntı
 * (kademeler, şartlar, canlı hesap) Detay penceresindedir — sayfanın başında
 * hesap ekranının tamamını çizmek kampanya ızgarasını bozuyordu (Betrun,
 * 16.10.2026).
 */

const BONUS_FILTER_VERTICAL = { casino: 'casino', sports: 'sports' };

// Birleşik kart her süzgeçte görünür; ayrık kart yalnızca kendi süzgecinde.
export const lossBlockMatchesFilter = (block, filterId) => {
  const vertical = BONUS_FILTER_VERTICAL[filterId];
  return !vertical || block.vertical === 'all' || block.vertical === vertical;
};

const maxTierPercent = (block) => (block.tiers || []).reduce(
  (max, tier) => Math.max(max, Number(tier.percent) || 0), 0);

export const lossTileState = (data, block) => {
  const c = block.claimable || {};
  const repeatable = isRepeatable(data.period);
  const ready = !!c.can_claim && Number(c.amount) > 0;
  let status = 'Hak doğmadı';
  if (ready) status = 'Hemen alınabilir';
  else if (c.funding_notice) status = 'Net para girişi yetersiz';
  else if (repeatable && Number(c.claimed_amount) > 0) status = 'Yeni kayıp bekleniyor';
  else if (!repeatable && c.already_claimed) status = 'Bu dönem alındı';
  return {
    ready,
    status,
    claimed: !repeatable && !!c.already_claimed,
    amount: Number(c.amount) || 0,
    maxPercent: maxTierPercent(block),
  };
};

const actionLabel = (state) => {
  if (state.claimed) return 'Alındı';
  return state.ready ? `Bonusu Al (${money(state.amount)})` : 'Bonusu Al';
};

/** Görselin yerini tutan kapak; `<button>` içinde durduğu için yalnızca span. */
export const LossBonusArt = ({ data, block }) => {
  const state = lossTileState(data, block);
  return (
    <span className="loss-bonus-art" data-bhs-vertical={block.vertical}>
      <span className="loss-bonus-art-badge">{periodLabel(data.period, data.rolling_hours)}</span>
      <span className="loss-bonus-art-icon" aria-hidden="true"><RotateCcw size={20} /></span>
      <span className="loss-bonus-art-kicker">{verticalTitle(data, block)}</span>
      <span className="loss-bonus-art-value">
        {state.ready ? money(state.amount) : `%${state.maxPercent}`}
      </span>
      <span className="loss-bonus-art-caption">
        {state.ready ? 'Talep etmeye hazır' : 'Kademeli kayıp iadesi'}
      </span>
      <span className={`loss-bonus-art-status ${state.ready ? 'is-ready' : ''}`}>
        {state.status}
      </span>
    </span>
  );
};

/**
 * Panelden yüklenen kart görseli (Ayarlar → Kayıp Bonusu → Bonuslar Sayfası
 * Kart Görseli). Görsel kampanya bannerı gibi tasarımın kendisidir; üstüne
 * yalnızca bonus alınabilir olduğunda tutar rozeti biner — oyuncu Detay'ı
 * açmadan ne kadar alacağını görür.
 */
const ReadyPill = ({ state }) => (
  state.ready ? (
    <span className="loss-bonus-ready-pill">{money(state.amount)} hazır</span>
  ) : null
);

/** Görsel ızgaralı şemalar (Betrun): kampanya kartının aynısı. */
export const LossBonusImageTile = ({
  data, block, busy, onClaim, onDetail,
}) => {
  const state = lossTileState(data, block);
  const title = verticalTitle(data, block);
  return (
    <div className="bonus-image-item loss-bonus-item" data-bhs-loss-bonus={block.vertical}>
      <button
        type="button"
        className="bonus-image-card"
        onClick={() => onDetail(block.vertical)}
        aria-label={`${title} detaylarını göster`}
      >
        {block.cover_image ? (
          <>
            <img src={resolveApiAssetUrl(block.cover_image)} alt={title} loading="lazy" />
            <ReadyPill state={state} />
          </>
        ) : (
          // Kurulumun özel CSS'i görselsiz kampanya kutusunu ölçülendirir;
          // kapak aynı sınıfla o ölçüyü devralır.
          <span className="bonus-image-card-fallback loss-bonus-frame">
            <LossBonusArt data={data} block={block} />
          </span>
        )}
      </button>
      <div className="bonus-image-actions">
        <button
          type="button"
          disabled={busy || !state.ready}
          onClick={() => onClaim(block.vertical)}
          className="gold-btn bonus-image-action"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {state.claimed ? 'Alındı' : 'Bonusu Al'}
        </button>
        <button
          type="button"
          onClick={() => onDetail(block.vertical)}
          className="gold-outline-btn bonus-image-action"
        >
          Detay
        </button>
      </div>
    </div>
  );
};

/** Klasik kart ızgarası (Merit ve diğerleri): görsel alanı + gövde. */
export const LossBonusCard = ({
  data, block, busy, onClaim, onDetail,
}) => {
  const state = lossTileState(data, block);
  const c = block.claimable || {};
  const repeatable = isRepeatable(data.period);
  return (
    <div className="pusu-card overflow-hidden group loss-bonus-item" data-bhs-loss-bonus={block.vertical}>
      <div className="loss-bonus-media aspect-[16/9]">
        {block.cover_image ? (
          <>
            <img
              src={resolveApiAssetUrl(block.cover_image)}
              alt={verticalTitle(data, block)}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <ReadyPill state={state} />
          </>
        ) : <LossBonusArt data={data} block={block} />}
      </div>
      <div className="p-5">
        <h3 className="font-bold text-lg text-white">{verticalTitle(data, block)}</h3>
        <p className="mt-2 text-sm leading-6 text-gray-400">
          Net kaybınız üzerinden kademeli iade.
          {' '}
          {repeatable
            ? 'Hak doğduğu anda buradan alabilirsiniz.'
            : 'Dönem tamamlanınca buradan talep edilir.'}
        </p>
        <div className="grid grid-cols-3 gap-2 text-xs mt-3 pt-3 border-t border-white/5">
          <div>
            <div className="text-gray-500">Net Kayıp</div>
            <div className="font-bold text-white">{money(c.net_loss)}</div>
          </div>
          <div>
            <div className="text-gray-500">Bonus</div>
            <div className="font-bold text-green-400">{money(c.amount)}</div>
          </div>
          <div>
            <div className="text-gray-500">Çevrim</div>
            <div className="font-bold text-yellow-400">
              {block.wager_multiplier > 0 ? `x${block.wager_multiplier}` : 'Yok'}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            type="button"
            disabled={busy || !state.ready}
            onClick={() => onClaim(block.vertical)}
            className="gold-btn flex-1 py-2.5 rounded-lg text-sm leading-tight flex items-center justify-center gap-1 disabled:opacity-50"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {actionLabel(state)}
          </button>
          <button
            type="button"
            onClick={() => onDetail(block.vertical)}
            className="gold-outline-btn px-4 py-2.5 rounded-lg text-sm"
            aria-label={`${verticalTitle(data, block)} detaylarını göster`}
          >
            Detay
          </button>
        </div>
      </div>
    </div>
  );
};
